import {addDays, formatISO, startOfDay, subDays} from 'date-fns';
import {
  assertEvent,
  assign,
  fromPromise,
  raise,
  setup,
  type SnapshotFrom,
} from 'xstate';

import {
  createEvent,
  getAvailability,
  getAvailabilityRange,
  noAvailability,
  updateEvent,
} from '../../services/schedularService';

import type {
  IBookingMachineContext,
  IBookingMachineEvents,
  IBookingMachineInput,
  IBookingMachineOutput,
} from './bookingMachine.types';
import {canNavigateNextWeek} from '../../utils/booking';
import type {IAdditionalQuestionAnswer} from '../../types/dto/IAdditionalQuestionAnswer';
import {TZDate} from '@date-fns/tz';
import type {AxiosError} from 'axios';
import {captureSentryMessage} from 'src/utils/sentry';


export const bookingMachine = setup({
  types: {
    context: {} as IBookingMachineContext,
    events: {} as IBookingMachineEvents,
    input: {} as IBookingMachineInput,
    output: {} as IBookingMachineOutput,
  },
  actors: {
    getAvailabilityRange: fromPromise(
      async ({
        input,
      }: {
        input: {templateId: string; eventId?: string; sessionId?: string};
      }) =>
        getAvailabilityRange(input.templateId, {
          event_id: input.eventId,
          session_id: input.sessionId,
        }),
    ),
    getAvailability: fromPromise(
      async ({
        input,
      }: {
        input: {
          templateId: string;
          timezone: string;
          eventId?: string;
          sourceId?: string;
          startDate: string;
          endDate: string;
        };
      }) =>
        getAvailability(input.templateId, {
          viewer_timezone: input.timezone,
          event_id: input.eventId,
          source_id: input.sourceId,
          start_dt: input.startDate,
          end_dt: input.endDate,
        }),
    ),
    updateEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          eventId: string;
          date: string;
          startTime: string;
          timezone: string;
          attendee: string;
          firstAvailableDatetime: string;
        };
      }) =>
        updateEvent(input.eventId, {
          date: input.date,
          start_time: input.startTime,
          viewer_timezone: input.timezone,
          attendee: input.attendee,
          first_available_datetime: input.firstAvailableDatetime,
        }),
    ),
    sendNotification: fromPromise(
      ({
        input,
      }: {
        input: {
          templateId: string;
          guestName: string;
          guestEmail: string;
          guestPhone: string;
          sessionId?: string;
          guestTimezone: string;
        };
      }) =>
        noAvailability(input.templateId, {
          guest_name: input.guestName,
          guest_email: input.guestEmail,
          guest_phone: input.guestPhone,
          session_id: input.sessionId,
          guest_timezone: input.guestTimezone,
        }),
    ),
    createEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          templateId: string;
          timezone: string;
          date: string;
          startTime: string;
          guestName: string;
          guestEmail: string;
          guestPhone: string;
          firstAvailableDatetime: string;
          sessionId?: string;
          language: string;
          additionalQuestionAnswers: IAdditionalQuestionAnswer[];
        };
      }) => {
        return createEvent({
          template_id: input.templateId,
          viewer_timezone: input.timezone,
          date: input.date,
          start_time: input.startTime,
          guest_name: input.guestName,
          guest_email: input.guestEmail,
          guest_phone: input.guestPhone,
          first_available_datetime: input.firstAvailableDatetime,
          additional_question_answers: input.additionalQuestionAnswers,
          session_id: input.sessionId,
          guest_language: input.language,
        });
      },
    ),
  },
  guards: {
    isTimeSlotUnavailable: ({context}) => {
      const error = context.error as AxiosError<{errors: string[]}>;
      const errorKey = error?.response?.data?.errors?.[0];
      const unavailableTimeSlotErrorKeys = [
        'UnavailableTimeSlot',
        'TimeSlotNotAvailable',
      ] as const;
      return unavailableTimeSlotErrorKeys.includes(
        errorKey as typeof unavailableTimeSlotErrorKeys[number],
      );
    },
    isSlotsAvailableForCurrentWeek: ({context}) => {
      const {timeSlots, selectedStartDateOfWeek, availabilityRange} = context;

      if (!timeSlots || !selectedStartDateOfWeek || !availabilityRange) {
        throw new Error(
          'Time slots or selected start date of week or availability range is not found',
        );
      }

      const weekStart = formatISO(selectedStartDateOfWeek, {
        representation: 'date',
      });
      const weekEnd = formatISO(addDays(selectedStartDateOfWeek, 6), {
        representation: 'date',
      });

      const hasAvailableSlotsInCurrentWeek = Object.keys(
        timeSlots.free_time_slots || {},
      ).some((dateKey) => {
        return dateKey >= weekStart && dateKey <= weekEnd;
      });

      return hasAvailableSlotsInCurrentWeek;
    },
    isNewBooking: ({context}) => !context.eventDetails,
    isNoSlotsAvailableTillLastWeek: ({context}) => {
      const {timeSlots, selectedStartDateOfWeek, availabilityRange, timezone} =
        context;

      if (!timeSlots || !selectedStartDateOfWeek || !availabilityRange) {
        throw new Error(
          'Time slots or selected start date of week or availability range is not found',
        );
      }
      const noSlotsAvailable =
        Object.keys(timeSlots.free_time_slots || {}).length === 0;

      const canNavigateToNextWeek = canNavigateNextWeek(
        timezone,
        selectedStartDateOfWeek,
        availabilityRange,
        noSlotsAvailable,
      );

      return noSlotsAvailable && !canNavigateToNextWeek;
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QCMD2qDWBLAdlAdAIYBuhWANochVgC4CeASoXmADKqES5QDEEqHGHy5imYWkw8ipClRoNmrDlx4JRqAMaFaWQQG0ADAF0jxxKAAOqWHT04LIAB6IATAE58AVleuvAFgBGAA4AZi9gwMNw4IAaEHpEQND-T1cAdmD3d3Tc9y93Q2CAX2L4yWw8GTJKanI6JhYYFW48XjAAJw7UDvxLSloAMx6AW3wK6RIa+XrFJvZOVqh1HDFtXQMTM0drWw2HJGdELy90-Cjo4MN00OD-dNd4xIRA10NDc-9g9K+I-1Cbv5SuV0JUCBAdGAACpYEZgAAKWE0GE6vFgtEIHVoABFIQB5QYAdTAYAw+E0AAt5ttDrs7IJHC4EABaQKBM5fdKBdwANlCPKChh8oSeST++EMrh5wT8IRCHi8wJAEyqENo0NhCKRKI6-Eh5KprBpVhs9IOoCZzNCvnw6RO7y51t5XkCXlFCDe0XOPNePL97i+PNySpV4MhMLhiORqIA7iSyUInLRjSA6ftGYhma4svg7v4vO8XYZ8q63QkkmFAvh-D6Hv5UoYeUKSmVlaDpGqNZHtbH430OmBiCm0-YMyzrR9+elClkhTyMn53a5uaF8O5fNdfK4glF0iH26rw5qozrqnI6g0Wjx+IJhBoUeMD2H1RGtdHelNzwp6Fe8Cs1jo9hmMOprpocTJsquPxeNKNbbh42YiuWLzBAK+A8hEQb8q87JeKE+5SIeL7Hj2H6yLU36-nwnTdL0-Q6MMHRjKG+Cdq+J6dGeFGzD+ixqBo6xAVsJg7KBo7gYg9w8rmBYPIYQQ+DkjzIa6PJpHyVwYTkXzFgRYKsUe3bvlxMyXoskC8CBeziRaiB+hKARSt86SXA86RLq8Xhrg8Jy5OEQT3HpHaGW+p6ftxZlcBZ+iBOYtJiQyEkILk0kYa6qH+EK1zuB54S2gU9xZL4-h+EFRFdqFnHhaZDAtNFrhxSa1mJbZCDBHh+Vcr47LpBhYRLm8VZSvJfp+q4AKBa2LFsSRxnVReDAAKJdD0vADrQHT0FZZpjvyri2tmvWoYE-xvO5KnSvt7WodO7hsn4-xlc+FUcWR0wLfQADK5CoLQsC8LocKwD9tD4LAACuyAjHQ21ga1ATVsWkRFXh841kuDbeE567-NkPz4VNT4GcRRlheRNVfSD-2A3AINg5D0PJrFonNeaRxtT6EpNn6UQBlkAIY4UWPSjjoR449hOEc97GkfgNPA79ADCgiDFgTGAYIAOagroOaCrasjLDNns3d-hrv6YQCt8KRls8GQI7ykqzvOgQCk9xMvbL8sg8rOCq+r+xa0DdMQ1DMMifFrNjgKXk5ACRQCgKuRLlyVa9QUPj5ikGTuzNpOcQOsCUpA4PkGAABioyfQzdC6G0AhCCIqziI+UsezLxmF8XEClxXVc17QdfLAJGs4MBEdNTtSV3ftAKhK8OTSr5gTutnV0+Mu6X1qhucha9+BdxSJdl5XTHV2Hg-XjRPR9AMjHMUTeeVb0h-H33Z8D0P-5aKP4+NamCU2YQWSB8O4NxpSGFeNcesS47pnHXFcKIVwZyBF3iTZ+B84Dd17qfEYy1aKWQngAqOSVXY1jXFcXIroLhBGUnbB4+1GxOinOudwaDPadywUfHuJ9Rj4NWjFf+I4WrsxOnyRGmQ1KXSiH6FONomFBB9AUH0PJ2EdzJu9b8AA5X6WBBj0F4DgXR+iAASNhQZQFQEbEREF7gfGLFnO0jZ2o8lyquAsAZpx+juLyNRs1TzrU2owLhb8g601+uSfWTFrFAKSDI842QSoCiKL4fkKduS5mSJlee1sXF+Pzr0cGOB5pl1fN9X6p9glF24b3NaYBBhgFoJSAAguTD6lSQk8OEJYmJY4yHuIiFcbM3Jrh2gxvZW4ERsxMO5Hhd281vxKBgPw3UgStpEOEbEhAe0DrfFdIUPwG9zrPF3GbdwVxrTbnzPmXw7sjG6H0fge+hjjH0HphfXpSVlxNltEGEqIQgx+GtEuSUZxiwhEGZEH0vjJb6XuXot5odGZDxvI3e8EgibwseUi2u-Fm6CU2KYDZgCxzLhclzB4eMYiZH8EuFI0kdwnVdJlSIOQ7mvPeciq+K06J31GK3OFHKcWXz-CPfYf8WZT1atuV4+BrSaWiDBcBHk3gSklH6EazpxrsoeW86+uosWIoHp86VJVPAuWGaEW4Vq+QgoyBKOBeF8xXBlECWF0hQy4N4MgQgyITXsxKvtT05z-Jo0OnStCeYQhhFQhhVw7tPX8ueffTl4chEkqSvmOVVwCwAj6nyTIq9577TwsEV1+Y7STRBG3RNTFU0ir4A3O8zcHwsVrWMYVX8xVCSJemkh0rgjeElPPfkfISr3CQs8eOq5bjSgLb1BSCany4PrSi-Vt8GL8rbcu-lna8UAXFcJPtUr2a3DOLcdcfJ6VTJOqvDeuZ5z8iKO1aI9Yl1SxXfqwhx64bsznd4VIDwhmoX5LbRAVrci5i+PHNkyQ2XuqqO2-AX7BGSt-UyWS5xXUWuGtuOIyEINnDuGEFysGxZ7gQwQJDazz6M14Cm4V-qIJoTtNzHx7J-iTvAxNNcy4JonV6tECj1b9JIaKSUiq5TaB1IaU0ikrTNE8XwD04l-a-3uFXNkNkGExapAKCvAjy4zanB8fyK10CKOtiMRAOAjhQxoeNpaIIg67QFmuMkDwaUwMIHORyIZ04Uj-FLfMtpiz5hUQczYySuZ8jz3eE2ZceEAzujZELf4sGSrz2nAEfJz9ItbKtOS1zDoPPOldClr4ac+QaZ+L4K9wm2xtyfvvBZPEIuRxPUyb46ESrvCdNybktKLrZHOBucB85EIthE8FdBLXQttfMhAfLfTXYSldFuW47wSrfBBUKXM7w8NNmRjKXLc3FMNBWctpKpx9rZBiFKV4TlXEXUgycKUnHuTTPjZR9u-iqrzYaFJ+AHX0NxP2q6fj4Q3hFSG3bfkZso1ltnXG07stWsXZGJYBgV3Wourld1LcWkZQGbtodLG0brRBADKon7zWvbax9lEkYo8cfsyyJ4DCQo-DPrIcctwSdbTZE9PORsrtUecOqW-XBtHcV4FZ11zIa3bjC6bCcZ7dsoXeF5JlOc2RJSKlp3vWWr8um4MuyDxzSQzXnDtC5fM8l5K3FgWLRGNZXY22SHMw3s20cA4YDo3V8ukgbnwH4ec3w1KZCYW4odnjeSwRhdN8q6iC6NKCZ03uQeXgZE8JewoHnI-jXSZ4SITp3gjKC+L084m2mlM1FJjpkuulZ7IftXqrHuYMPCEuPkk4NODMgXdAIBMk8EHR3MVg5vJ6g4QDBM288h+ehdKEeSKXoEUOiMVa5G931gmrpoTQcBgfT8t9n7wE0jvsniy6HKBGwix3uHdJs64GssUNfvw-sBj-EM624bkg7xoPEE58x5x1c3AaxNME5sxLVUkdUEUs8zpVx0t45so7cUswhzUrV8hUggwY44DHl74ECVxbQAspQN5yM6EkgX9Q8rVXUTptx1wR9GtBVdVV0eAiDlxqwR0XJUDV9kJlw-BRsy0kc54zN8C9UeUECBQoJFUIdtx1U7UwVHUbYXVtxd8eBcEs8swS0PBUh0oYIUg8xV4TovJOc4NPMF91C8AV1CCLcotkpjNIEk4pwMJUhV5xpVxXRIEZRyMghgwfskM905c7CtlBC7ECw7gTpQNVJV5ORbRBtoFhp6xX8iYkN9Us8BRzUl55wfhOQuNtknUuYhR7d-JkgmDt0P1+UaMB4s8rUzYjoyir9FESduM7QtcMDh0rgkkrCoAV0a93o684QpMMiRsmwucF9uCwDtlPN0IuQbp1wXQfRShSggA */
  context: ({input}) => input,
  id: 'booking',
  initial: 'availabilityRangeLoading',
  states: {
    availabilityRangeLoading: {
      invoke: {
        id: 'booking.availabilityRangeLoading:invocation[0]',
        input: ({context: {eventDetails, session, eventTemplate}}) => {
          const templateId =
            eventTemplate?.id || eventDetails?.scheduled_from_template_id;
          if (templateId) {
            return {
              templateId,
              eventId: eventDetails?.id,
              sessionId: session?.id,
            };
          }
          throw new Error('Template id not found');
        },
        onDone: {
          target: 'dateTimePicker',
          actions: assign({
            availabilityRange: ({event}) => event.output,
            selectedStartDateOfWeek: ({event, context}) => {
              const startAt = event.output.start_at;
              if (!startAt || isNaN(startAt)) {
                captureSentryMessage(
                  'Invalid start_at from Availability Range API',
                  {
                    level: 'error',
                    tags: {
                      product: 'scheduler',
                    },
                    extra: {
                      apiParams: {
                        templateId: context.eventTemplate,
                        sessionId: context.session?.id,
                        eventId: context.eventDetails?.id,
                      },
                      timezone: context.timezone,
                      rangeApiResponse: event.output,
                      eventDetails: context.eventDetails,
                      eventTemplate: context.eventTemplate,
                    },
                  },
                );
              }
              return new TZDate(startAt * 1000, context.timezone);
            },
          }),
        },
        onError: {
          target: 'availabilityRangeError',
          actions: assign({
            error: ({event}) => event.error,
          }),
        },
        src: 'getAvailabilityRange',
      },
    },
    dateTimePicker: {
      initial: 'availabilityLoading',
      on: {
        'startDateOfWeek.change': {
          target: '#booking.dateTimePicker.availabilityLoading',
          actions: assign({
            selectedStartDateOfWeek: ({event}) => event.date,
            selectedTimeSlot: undefined,
          }),
        },
        'date.change': {
          actions: assign({
            selectedDate: ({event}) => event.date,
            selectedTimeSlot: undefined,
          }),
          target: 'dateTimePicker.availabilitySlots',
        },
        'week.next': {
          actions: raise(({context: {selectedStartDateOfWeek, timezone}}) => {
            if (!selectedStartDateOfWeek) {
              throw new Error('Selected start date of week is not found');
            }
            return {
              type: 'startDateOfWeek.change',
              date: new TZDate(addDays(selectedStartDateOfWeek, 7), timezone),
            };
          }),
        },
        'week.prev': {
          actions: raise(({context: {selectedStartDateOfWeek, timezone}}) => {
            if (!selectedStartDateOfWeek) {
              throw new Error('Selected start date of week is not found');
            }
            return {
              type: 'startDateOfWeek.change',
              date: new TZDate(subDays(selectedStartDateOfWeek, 7), timezone),
            };
          }),
        },
      },
      states: {
        availabilityLoading: {
          invoke: {
            input: ({
              context: {
                eventDetails,
                eventTemplate,
                timezone,
                session,
                availabilityRange,
                selectedStartDateOfWeek,
              },
            }) => {
              const templateId =
                eventTemplate?.id || eventDetails?.scheduled_from_template_id;
              try {
                if (
                  templateId &&
                  availabilityRange &&
                  selectedStartDateOfWeek
                ) {
                  const currentDate = selectedStartDateOfWeek;
                  const addedDate = addDays(currentDate, 7);
                  const startDate = formatISO(startOfDay(currentDate), {
                    representation: 'complete',
                  });
                  const endDate = formatISO(startOfDay(addedDate), {
                    representation: 'complete',
                  });

                  return {
                    templateId,
                    timezone,
                    eventId: eventDetails?.id,
                    sourceId: session?.source_id,
                    startDate,
                    endDate,
                  };
                }
                throw new Error('Template id or availabilityRange not found');
              } catch (error) {
                if (selectedStartDateOfWeek) {
                  captureSentryMessage(
                    'Error setting availability api params',
                    {
                      level: 'error',
                      tags: {product: 'scheduler'},
                      extra: {
                        error,
                        timezone,
                        eventDetails,
                        eventTemplate,
                        session,
                        availabilityRange,
                        selectedStartDateOfWeek,
                        weekStartDate: startOfDay(selectedStartDateOfWeek),
                        weekEndDate: startOfDay(
                          addDays(selectedStartDateOfWeek, 7),
                        ),
                      },
                    },
                  );
                }
                throw error;
              }
            },
            onDone: {
              target: 'availabilityLoaded',
              actions: assign({
                timeSlots: ({context, event}) => ({
                  free_time_slots: {
                    ...context.timeSlots?.free_time_slots,
                    ...event.output.free_time_slots,
                  },
                }),
                selectedDate: ({context, event}) => {
                  if (!context.selectedStartDateOfWeek) {
                    throw new Error('Selected start date of week is not found');
                  }

                  return Object.keys(event.output?.free_time_slots || {})[0];
                },
                selectedTimeSlot: undefined,
              }),
            },
            onError: {
              target: 'availabilityError',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'getAvailability',
          },
        },

        availabilityLoaded: {
          always: [
            {
              target: 'availabilitySlots',
              guard: {
                type: 'isSlotsAvailableForCurrentWeek',
              },
            },
            {
              target: 'availabilityNotify',
              reenter: true,
              guard: 'isNoSlotsAvailableTillLastWeek',
            },
            {
              target: 'availabilityEmpty',
              reenter: true,
            },
          ],
        },

        availabilityError: {
          on: {
            retry: {
              target: 'availabilityLoading',
            },
          },
        },

        availabilitySlots: {
          on: {
            'timeslot.submit': [
              {
                target: '#booking.bookingForm',
                actions: assign({
                  selectedTimeSlot: ({event}) => event.timeSlot,
                }),
                guard: {
                  type: 'isNewBooking',
                },
              },
              {
                target: 'timeslotConfirmation',
                actions: assign({
                  selectedTimeSlot: ({event}) => event.timeSlot,
                }),
              },
            ],
          },
        },

        availabilityEmpty: {},

        timeslotConfirmation: {
          on: {
            'timeslot.confirm': {
              target: 'rescheduleFormSubmitting',
            },
            'timeslot.submit': {
              target: 'timeslotConfirmation',
              actions: assign({
                selectedTimeSlot: ({event}) => event.timeSlot,
              }),
            },
          },
        },

        rescheduleFormSubmitting: {
          invoke: {
            input: ({
              context: {
                eventDetails,
                timeSlots,
                attendee,
                selectedDate,
                selectedTimeSlot,
                timezone,
              },
            }) => {
              const firstDate = Object.keys(
                timeSlots?.free_time_slots || {},
              )[0];
              const firstDateTimeList = timeSlots?.free_time_slots[firstDate];
              const startTime = firstDateTimeList?.[0].start_time;

              if (eventDetails?.id && selectedDate) {
                return {
                  eventId: eventDetails?.id,
                  date: selectedDate,
                  startTime: selectedTimeSlot?.start_time || '',
                  timezone: timezone || '',
                  attendee: attendee || '',
                  firstAvailableDatetime: `${firstDate} ${startTime}`,
                };
              }
              throw new Error('Event id not found');
            },
            onDone: {
              target: '#booking.bookingSuccess',
              actions: assign({
                eventDetails: ({event}) => event.output,
              }),
            },
            onError: {
              target: 'rescheduleFormError',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'updateEvent',
          },
        },

        rescheduleFormError: {
          always: [
            {
              target: 'unavailableTimeSlotForReschedule',
              guard: 'isTimeSlotUnavailable',
            },
            {
              target: 'retryReschedule',
              actions: assign({
                selectedTimeSlot: undefined,
              }),
              reenter: true,
            },
          ],
        },

        availabilityNotify: {
          on: {
            'notifyHost.go': {
              target: '#booking.notify',
              reenter: true,
            },
          },
        },

        retryReschedule: {
          on: {
            'timeslot.confirm': {
              target: 'rescheduleFormSubmitting',
              reenter: true,
            },
          },
        },

        unavailableTimeSlotForReschedule: {
          on: {
            'refetchAvailabilityForReschedule.go': {
              target: 'availabilityLoading',
              reenter: true,
            },
          },
        },
      },
    },
    availabilityRangeError: {
      on: {
        retry: {
          target: 'availabilityRangeLoading',
        },
      },
    },
    bookingSuccess: {
      type: 'final',
      entry: assign({
        finalState: 'BookingSuccess',
      }),
    },
    notifySuccess: {
      type: 'final',
      entry: assign({
        finalState: 'NotifySuccess',
      }),
    },
    notify: {
      initial: 'form',
      states: {
        form: {
          on: {
            'notify.submit': {
              target: 'submitting',
            },
          },
        },

        submitting: {
          invoke: {
            input: ({
              context: {session, eventTemplate, eventDetails, timezone},
              event,
            }) => {
              assertEvent(event, 'notify.submit');
              const templateId =
                eventTemplate?.id || eventDetails?.scheduled_from_template_id;
              if (!templateId) {
                throw new Error('Template id not found');
              }
              return {
                templateId,
                guestName: event.guestName,
                guestEmail: event.guestEmail,
                guestPhone: event.guestPhone,
                sessionId: session?.id,
                guestTimezone: timezone,
              };
            },
            onDone: {
              target: '#booking.notifySuccess',
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'sendNotification',
          },
        },

        error: {
          on: {
            'notify.submit': {
              target: 'submitting',
            },
          },
        },
      },
    },
    bookingForm: {
      initial: 'form',
      on: {
        back: {
          target: '#booking.dateTimePicker.availabilitySlots',
        },
      },
      states: {
        form: {
          on: {
            'form.submit': {
              target: 'submitting',
            },
          },
        },
        submitting: {
          invoke: {
            input: ({
              context: {
                eventTemplate,
                timezone,
                selectedDate,
                selectedTimeSlot,
                timeSlots,
                session,
                language,
              },
              event,
            }) => {
              assertEvent(event, 'form.submit');
              const firstDate = Object.keys(
                timeSlots?.free_time_slots || {},
              )[0];
              const firstDateTimeList = timeSlots?.free_time_slots[firstDate];
              const startTime = firstDateTimeList?.[0].start_time;
              if (eventTemplate?.id && selectedDate && selectedTimeSlot) {
                return {
                  templateId: eventTemplate?.id,
                  timezone,
                  date: selectedDate,
                  startTime: selectedTimeSlot.start_time,
                  guestName: event.guestName,
                  guestEmail: event.guestEmail,
                  guestPhone: event.guestPhone,
                  firstAvailableDatetime: `${firstDate} ${startTime}`,
                  sessionId: session?.id,
                  language,
                  additionalQuestionAnswers:
                    event.additionalQuestionAnswers || [],
                };
              }
              throw new Error('Template id, date or time not found');
            },
            onDone: {
              target: '#booking.bookingSuccess',
              actions: assign({
                eventDetails: ({event}) => event.output,
              }),
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'createEvent',
          },
        },
        error: {
          always: [
            {
              target: 'unavailableTimeSlot',
              guard: 'isTimeSlotUnavailable',
              reenter: true,
            },
            'retrySubmit',
          ],
        },
        retrySubmit: {
          on: {
            'form.submit': {
              target: 'submitting',
              reenter: true,
            },
          },
        },
        unavailableTimeSlot: {
          on: {
            'refetchAvailability.go': {
              target: '#booking.dateTimePicker.availabilityLoading',
              reenter: true,
            },
          },
        },
      },
    },
  },
  output: ({context: {eventDetails, finalState}}) => ({
    eventDetails,
    finalState,
  }),
});

export type IBookingMachineSnapshot = SnapshotFrom<typeof bookingMachine>;
